import { Download, MessageSquare, Printer } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/toast"
import { bannerFor } from "@/lib/api-error-message"

import { useMarkMessagesReadMutation, usePostMessageMutation } from "../api"
import { useReviewCommentsQuery } from "../api/use-review-comments"
import type { FindingId, Message, Review, ReviewId } from "../data/review-data"
import { MessagesSheet } from "../messages/messages-sheet"

import { useReportExport, type ExportAudience, type ExportFormat } from "./api/use-report-export"
import { SegmentButton, Segmented } from "./segmented"

const EMPTY_MESSAGES: readonly Message[] = []

const FORMAT_LABEL: Record<ExportFormat, string> = { pdf: "PDF", csv: "CSV", json: "JSON" }

export interface ReportActionsProps {
  reviewId: ReviewId
  review: Review
  /** Each finding's number on the report, which the conversation panel shows. */
  findingNumbers: ReadonlyMap<FindingId, number>
  /** Staff may export the reviewer view as well as the client's. */
  mayExportReviewerView: boolean
  /** Post to the conversation and mark it read. A viewer only reads it. */
  mayConverse: boolean
  /** Bring a finding up in the report, from the conversation panel. */
  onFocusFinding: (findingId: FindingId) => void
}

/**
 * The report's own actions, at the right of its top bar: the conversation,
 * printing, and the three downloads — as the legacy console has them.
 *
 * The downloads are of the same object the screen renders. "Client view" is
 * what the client receives; "Reviewer view" adds what only staff see, and is
 * offered to staff alone — the server gives a client the client view
 * whatever is asked for, so a toggle would only offer a choice it overrides.
 */
export function ReportActions({
  reviewId,
  review,
  findingNumbers,
  mayExportReviewerView,
  mayConverse,
  onFocusFinding,
}: ReportActionsProps): JSX.Element {
  const { toast } = useToast()
  const [messagesOpen, setMessagesOpen] = React.useState(false)
  const [audience, setAudience] = React.useState<ExportAudience>("client")

  const { data: conversation } = useReviewCommentsQuery(reviewId)
  const messages = conversation?.messages ?? EMPTY_MESSAGES
  // Unread is counted for the reader's side, and a reader who may not post
  // may not mark it read either: a badge they can never clear only nags.
  const unread = mayConverse ? messages.filter((m) => m.unread).length : 0

  const postMessage = usePostMessageMutation()
  const markRead = useMarkMessagesReadMutation()

  const exporter = useReportExport(reviewId, (error, format) => {
    const banner = bannerFor(error, `the ${FORMAT_LABEL[format]} report`)
    if (!banner.title) return
    toast({ tone: "error", title: "The download could not be prepared", body: banner.body })
  })
  // The client view, whatever a reader who cannot choose has left selected.
  const exportAs: ExportAudience = mayExportReviewerView ? audience : "client"

  // Numbered as the report numbers them, by position on the label. The review
  // payload's own numbers follow its order instead, and a panel calling a
  // finding "1" that the report beside it calls "3" sends the reader to the
  // wrong card.
  // Only the findings someone has written about, as the legacy console's
  // filter offers them: a chip per finding on the report would bury the few
  // threads there are under ones with nothing in them.
  const findings = React.useMemo(() => {
    const discussed = new Set(messages.map((m) => m.findingId).filter((id) => id !== null))
    return review.findings
      .filter((f) => discussed.has(f.id))
      .map((f) => ({ ...f, number: findingNumbers.get(f.id) ?? f.number }))
      .sort((a, b) => a.number - b.number)
  }, [review.findings, findingNumbers, messages])

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span className="relative">
        <Button
          size="sm"
          leadingIcon={<MessageSquare aria-hidden="true" />}
          onClick={() => {
            setMessagesOpen(true)
          }}
          aria-label={unread > 0 ? `Messages, ${String(unread)} unread` : "Messages"}
        >
          Messages
        </Button>
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full border-2 border-surface bg-err px-1 text-[9px] font-semibold leading-3 text-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </span>

      <Button
        size="sm"
        leadingIcon={<Printer aria-hidden="true" />}
        onClick={() => {
          window.print()
        }}
      >
        Print
      </Button>

      {mayExportReviewerView && (
        <div className="flex items-center gap-1.5">
          <span className="text-caption font-medium text-text-3">Export</span>
          <Segmented label="Export">
            <SegmentButton
              pressed={audience === "client"}
              onClick={() => {
                setAudience("client")
              }}
            >
              Client view
            </SegmentButton>
            <SegmentButton
              pressed={audience === "reviewer"}
              onClick={() => {
                setAudience("reviewer")
              }}
            >
              Reviewer view
            </SegmentButton>
          </Segmented>
        </div>
      )}

      {(["pdf", "csv", "json"] as const).map((format) => (
        <Button
          key={format}
          size="sm"
          // The report object itself leads, as on the legacy console.
          variant={format === "json" ? "primary" : "default"}
          loading={exporter.pending === format}
          // One download at a time: they share the server's renderer.
          disabled={exporter.pending !== null && exporter.pending !== format}
          leadingIcon={<Download aria-hidden="true" />}
          aria-label={`Download the ${exportAs} view as ${FORMAT_LABEL[format]}`}
          onClick={() => {
            exporter.download(format, exportAs)
          }}
        >
          {FORMAT_LABEL[format]}
        </Button>
      ))}

      <MessagesSheet
        open={messagesOpen}
        onOpenChange={setMessagesOpen}
        messages={messages}
        findings={findings}
        activeFindingId={null}
        clientAttached={review.client !== null}
        archived={review.assignment.archived}
        onFocusFinding={(findingId) => {
          setMessagesOpen(false)
          onFocusFinding(findingId)
        }}
        onSend={
          mayConverse
            ? ({ body, findingId }) => {
                postMessage.mutate({ reviewId, body, findingId })
              }
            : undefined
        }
        onRetry={(message) => {
          // As in the workspace: the failed row carries the only copy of what was typed.
          postMessage.mutate({
            reviewId,
            body: message.body,
            findingId: message.findingId,
            replaces: message.id,
          })
        }}
        onMarkRead={
          mayConverse
            ? (findingId) => {
                markRead.mutate({ reviewId, findingId })
              }
            : undefined
        }
      />
    </div>
  )
}
